import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import { clsx } from 'clsx';

interface TabsContextValue {
  value: string;
  onChange: (value: string) => void;
}

const TabsContext = createContext<TabsContextValue | null>(null);

function useTabsContext() {
  const context = useContext(TabsContext);
  if (!context) {
    throw new Error('Tabs components must be used within a Tabs provider');
  }
  return context;
}

interface TabsProps {
  defaultValue?: string;
  value?: string;
  onChange?: (value: string) => void;
  children: ReactNode;
  className?: string;
}

export function Tabs({
  defaultValue = '',
  value: controlledValue,
  onChange,
  children,
  className,
}: TabsProps) {
  const [internalValue, setInternalValue] = useState(defaultValue);
  const value = controlledValue ?? internalValue;

  const handleChange = useCallback((newValue: string) => {
    if (onChange) {
      onChange(newValue);
    } else {
      setInternalValue(newValue);
    }
  }, [onChange]);

  return (
    <TabsContext.Provider value={{ value, onChange: handleChange }}>
      <div className={className}>{children}</div>
    </TabsContext.Provider>
  );
}

interface TabsListProps {
  children: ReactNode;
  className?: string;
  variant?: 'default' | 'pills' | 'underline';
}

export function TabsList({ children, className, variant = 'default' }: TabsListProps) {
  const variantClasses = {
    default: 'inline-flex items-center gap-1 p-1 rounded-lg bg-gray-100',
    pills: 'inline-flex items-center gap-2',
    underline: 'flex border-b border-gray-200 gap-0',
  };

  return (
    <div className={clsx(variantClasses[variant], className)} role="tablist">
      {children}
    </div>
  );
}

interface TabProps {
  value: string;
  children: ReactNode;
  className?: string;
  icon?: React.ComponentType<{ className?: string }>;
  disabled?: boolean;
}

export function Tab({ value, children, className, icon: Icon, disabled = false }: TabProps) {
  const context = useTabsContext();
  const isActive = context.value === value;

  return (
    <button
      role="tab"
      aria-selected={isActive}
      aria-disabled={disabled}
      tabIndex={isActive ? 0 : -1}
      onClick={() => !disabled && context.onChange(value)}
      className={clsx(
        'inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-1',
        isActive
          ? 'bg-white text-gray-900 shadow-sm'
          : 'text-gray-600 hover:text-gray-900 hover:bg-white/50',
        disabled && 'opacity-50 cursor-not-allowed',
        className
      )}
    >
      {Icon && <Icon className={clsx('h-4 w-4', isActive ? 'text-amber-600' : 'text-gray-400')} />}
      {children}
    </button>
  );
}

interface TabContentProps {
  value: string;
  children: ReactNode;
  className?: string;
  forceMount?: boolean;
}

export function TabContent({ value, children, className, forceMount = false }: TabContentProps) {
  const context = useTabsContext();
  const isActive = context.value === value;

  if (!isActive && !forceMount) {
    return null;
  }

  return (
    <div
      role="tabpanel"
      aria-hidden={!isActive}
      className={clsx(
        'mt-4',
        isActive ? 'animate-in fade-in duration-200' : 'hidden',
        className
      )}
    >
      {children}
    </div>
  );
}

// Underline variant tabs (alternative style)
interface UnderlineTabProps {
  value: string;
  children: ReactNode;
  className?: string;
  icon?: React.ComponentType<{ className?: string }>;
  badge?: number | string;
}

export function UnderlineTab({ value, children, className, icon: Icon, badge }: UnderlineTabProps) {
  const context = useTabsContext();
  const isActive = context.value === value;

  return (
    <button
      role="tab"
      aria-selected={isActive}
      tabIndex={isActive ? 0 : -1}
      onClick={() => context.onChange(value)}
      className={clsx(
        'relative inline-flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-inset',
        isActive
          ? 'text-amber-600'
          : 'text-gray-500 hover:text-gray-700',
        className
      )}
    >
      {Icon && <Icon className={clsx('h-4 w-4', isActive ? 'text-amber-600' : 'text-gray-400')} />}
      {children}
      {badge !== undefined && (
        <span className={clsx(
          'ml-1 rounded-full px-2 py-0.5 text-xs font-medium',
          isActive ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'
        )}>
          {badge}
        </span>
      )}
      {isActive && (
        <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-amber-600 rounded-full" />
      )}
    </button>
  );
}
